import React, {useEffect, useMemo, useState} from 'react';
import {GridColDef, GridPaginationModel} from '@mui/x-data-grid-pro';
import {useQuery} from "@apollo/client";
import _ from "lodash";
import {AssetIotAlertList} from "../../graphql/queries/assetIotAlertList";
import Table from "../Table";
import moment from "moment";
import {Chip} from "@mui/joy";
import {Box, Button} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSearchParams} from "react-router-dom";
import ListFilter from "../ListFilter";
import AcknowledgeIotAlertModal from "../AcknowledgeIotAlertModal";
import {IotAlertFilterInput, IotAlertStatus} from "../../codegen/graphql";
import {getInitialPagination, addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import {paginationLimit} from "../../config/paginationLimit";
import {useResizeObserver} from "../../hooks/useResizeObserver";

interface Props {
    assetLocationId: string;
}

interface AlertRow {
    id: string;
    title?: string | null;
    allowAcknowledge: boolean;
}

const AssetAlertList = ({assetLocationId}: Props) => {
    const {t} = useTranslation();
    const [urlParams, setUrlParams] = useSearchParams();
    const [ref, filterHeight] = useResizeObserver();
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true";
    const tableHeight = viewportHeight
        - (showEnvBanner ? 40 : 0)
        - filterHeight
        - 63 // Header
        - 50 // Tabs
        - 24 // Breadcrumbs
        - 70; // Toolbar-Box

    const defaultFilter: IotAlertFilterInput = useMemo(
        () => ({status: {equals: IotAlertStatus.UN_ACKNOWLEDGED}}),
        [],
    );

    const [userFilter, setUserFilter] = useState<IotAlertFilterInput>(defaultFilter);
    const [acknowledgeTarget, setAcknowledgeTarget] = useState<AlertRow | null>(null);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        getInitialPagination(paginationLimit, urlParams),
    );
    const [filterFormKey, setFilterFormKey] = useState(0);

    useEffect(() => {
        addToUrlParams(urlParams, paginationModel, setUrlParams);
    }, [paginationModel]); // eslint-disable-line react-hooks/exhaustive-deps

    const {data, loading} = useQuery(AssetIotAlertList, {
        variables: {
            id: assetLocationId,
            filter: userFilter,
            pagination: {
                start: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize,
            },
        },
        skip: !assetLocationId,
    });

    const alerts = _.get(data, 'assetLocation.iotAlertList.alerts', []);
    // No default: while a page change is in flight Apollo returns no data, and a 0 count
    // would make the grid clamp back to page 0. Table keeps the last known count instead.
    const totalAlerts = _.get(data, 'assetLocation.iotAlertList.total');

    const filterAttributes = useMemo(() => ([
        {
            index: 'search',
            name: t('Search'),
            type: 'string',
            disabled: false,
            inputType: 'text',
            list: false,
            value: '',
            optionValues: null,
            addOnAttribute: null,
        },
        {
            index: 'status',
            name: t('Status'),
            type: 'string',
            disabled: false,
            inputType: 'select',
            list: false,
            value: filterFormKey === 0 ? IotAlertStatus.UN_ACKNOWLEDGED : undefined,
            optionValues: [
                {value: IotAlertStatus.UN_ACKNOWLEDGED, name: t('Unacknowledged')},
                {value: IotAlertStatus.ACKNOWLEDGED, name: t('Acknowledged')},
                {value: IotAlertStatus.ARCHIVED, name: t('Archived')},
            ],
        },
    ]), [t, filterFormKey]);

    const handleStatusChange = (values: {[k: string]: any}) => {
        const status = values?.status;
        setUserFilter((prev) => {
            const next: IotAlertFilterInput = {...prev};
            if (status) {
                next.status = {equals: status as IotAlertStatus};
            } else {
                delete next.status;
            }
            return next;
        });
        setPaginationModel((prev) => ({...prev, page: 0}));
    };

    const debouncedSearch = useMemo(() => _.debounce((values: {query: string, fields: string[]}) => {
        const trimmed = (values?.query ?? '').trim();
        setUserFilter((prev) => {
            const next: IotAlertFilterInput = {...prev};
            if (trimmed) {
                next.search = trimmed;
            } else {
                delete next.search;
            }
            return next;
        });
        setPaginationModel((prev) => ({...prev, page: 0}));
    }, 400), []);

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const columns: GridColDef[] = [
        {
            field: 'date',
            headerName: t('Date'),
            flex: 1,
            disableColumnMenu: true,
            valueGetter: (_value: any, row: any) => moment(row.detectedAt).format('L'),
        },
        {
            field: 'time',
            headerName: t('Time'),
            flex: 1,
            disableColumnMenu: true,
            valueGetter: (_value: any, row: any) => moment(row.detectedAt).format('LT'),
        },
        {
            field: 'title',
            headerName: t('Title'),
            flex: 1,
            disableColumnMenu: true,
        },
        {
            field: 'message',
            headerName: t('Message'),
            flex: 2,
            disableColumnMenu: true,
        },
        {
            field: 'severity',
            headerName: t('Severity'),
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => {
                const severity = params.value as string;
                const colorMap: Record<string, 'danger' | 'warning'> = {
                    ERROR: 'danger',
                    WARNING: 'warning',
                };
                const color = colorMap[severity];
                if (!color) return null;
                return <Chip color={color}>{severity === 'ERROR' ? t('Alarm') : t('Warning')}</Chip>;
            },
        },
        {
            field: 'acknowledge',
            headerName: '',
            width: 160,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!params.row.allowAcknowledge}
                    onClick={(e) => {
                        e.stopPropagation();
                        setAcknowledgeTarget(params.row as AlertRow);
                    }}
                >
                    {t('Acknowledge')}
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{width: '100%'}}>
            <Box ref={ref} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '8px'}}>
                <ListFilter
                    key={filterFormKey}
                    attributes={filterAttributes}
                    onValuesChange={handleStatusChange}
                    onSearch={debouncedSearch}
                    searchParams={{query: '', fields: []}}
                    onReset={() => {
                        debouncedSearch.cancel();
                        setPaginationModel((prev) => ({...prev, page: 0}));
                        setUserFilter({});
                        setFilterFormKey((k) => k + 1);
                    }}
                    formStyle={{paddingTop: '0px'}}
                />
            </Box>
            <Box sx={{width: '100%', height: tableHeight}}>
                <Table
                    loading={loading}
                    data={alerts}
                    columns={columns}
                    rowCount={totalAlerts}
                    pagination={true}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    pageSizeOptions={[paginationLimit]}
                    onPaginationModelChange={setPaginationModel}
                    disableColumnFilter
                    disableColumnSorting
                    disableRowSelectionOnClick
                    disableColumnResize
                    disableColumnReorder
                    disableColumnPinning
                    hideFooterSelectedRowCount
                />
            </Box>
            <AcknowledgeIotAlertModal
                open={!!acknowledgeTarget}
                alert={acknowledgeTarget}
                onClose={() => setAcknowledgeTarget(null)}
            />
        </Box>
    );
};

export default AssetAlertList;
